/**
 * planningContextCore — non-component bits split out of PlanningContext.tsx
 * so that the .tsx file can export only the <PlanningProvider /> component
 * (keeps react-refresh/only-export-components happy).
 */
import { createContext, useContext } from "react";
import type { ContentBrief, ContentBriefPlan } from "@/lib/composerCore";

export type IntakeChecklist = {
  topic?: boolean;
  avatar?: boolean;
  publish_type?: boolean;
  include_excerpts?: boolean;
  outline?: boolean;
  approval?: boolean;
};

export type ApprovedBrief = {
  status?: string;
  topic?: string | null;
  audience_avatar_id?: string | null;
  publish_type_key?: string | null;
  core_message?: string | null;
  real_life_anchor?: string | null;
  include_excerpt_retrieval?: boolean;
  corpus_document_ids?: string[];
  outline?: {
    hook?: string | null;
    intro?: string | null;
    body?: string | null;
    conclusion?: string | null;
  } | null;
};

export type LiveBrief = {
  approved_brief?: Record<string, unknown> | null;
  intake_checklist?: Record<string, boolean> | null;
} | null;

export type PlanningContextValue = {
  workspaceId: string | null;
  brief: ContentBrief | null;
  latestPlan: ContentBriefPlan | null;
  onRefresh: () => Promise<void> | void;
  liveBrief: LiveBrief;
  setLiveBrief: (v: LiveBrief) => void;
  /** Called before regenerate/edit when an approved brief exists. */
  handleBeforeMutateApproved: () => Promise<boolean>;
};

export const PlanningCtx = createContext<PlanningContextValue | null>(null);

export const usePlanningContext = () => useContext(PlanningCtx);

export const GATHERS: Array<{ key: keyof IntakeChecklist; label: string; short: string }> = [
  { key: "topic", label: "Topic + real-life anchor", short: "Topic" },
  { key: "avatar", label: "Audience avatar", short: "Avatar" },
  { key: "publish_type", label: "Publish type", short: "Type" },
  { key: "include_excerpts", label: "Include Excerpts", short: "Excerpts" },
  { key: "outline", label: "Hook / Intro / Body / Conclusion outline", short: "Outline" },
  { key: "approval", label: "Explicit approval", short: "Approve" },
];

export const readIntakeState = (latestPlan: ContentBriefPlan | null) => {
  const raw = (latestPlan as { intake_state?: unknown } | null)?.intake_state;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { transcript: [], checklist: {} as IntakeChecklist, proposed: null as ApprovedBrief | null };
  }
  const state = raw as {
    transcript?: unknown[];
    checklist?: IntakeChecklist;
    approved_brief?: ApprovedBrief | null;
  };
  return {
    transcript: (state.transcript ?? []) as never[],
    checklist: state.checklist ?? {},
    proposed: state.approved_brief ?? null,
  };
};
